import { DetectorType } from "../../utils/enum";
import { clearLog, log, table } from "../../utils/log";
import { IS, calculateTime, createLargeObjectArray } from "../../utils/util";
import { Detector } from "../detector";

export default class extends Detector {

  largeObjectArray: any;
  maxPrintTime: number = 0; // 保留但不再作為唯一門檻（向後相容）

  // 新增平滑 / 動態判斷所需欄位
  private samples = 0;
  private warmupSamples = 3; // 前幾次視為暖身，不判定
  private logEma: number | null = null; // log() 花費時間的 EMA
  private tableEma: number | null = null; // table() 花費時間的 EMA
  private ratioBaselineEma: number | null = null; // 正常狀態下 table/log 比值的基準 EMA
  private readonly emaAlpha = 0.3; // 時間樣本 EMA 平滑係數
  private readonly baselineAlpha = 0.1; // 基準比值 EMA 平滑係數（較慢）
  private consecutiveBreaches = 0;
  private requiredConsecutiveBreaches = 2; // 需要連續命中次數以降低偶發尖峰
  private lastTriggerTime = 0;
  private cooldownMs = 5000; // 觸發後冷卻，避免短期重複觸發
  private readonly minTableMs = 40; // 絕對最小 table render 門檻（過小代表噪音）
  private readonly staticRatioThreshold = 8; // 與舊邏輯對齊（10）但稍微放寬；後續與動態基準取最大
  private readonly dynamicMultiplier = 3; // 相對基準倍數門檻

  private lastSampleTs = 0; // 上一次有效樣本時間
  private readonly gapThresholdMs = 5000; // 閒置/節流間隔判定為『回暖』的閾值
  private wasHidden = false; // 分頁曾經 hidden，用於回前景軟重置
  // 絕對差值相關 (D)
  private readonly minDeltaBase = 20; // 基礎差值門檻 (ms)
  private readonly minDeltaMax = 80; // 差值門檻上限 (ms)
  private readonly highRatioEmergency = 15; // 極端高比例時直接忽略差值

  constructor() {
    super({
      type: DetectorType.Performance,
      enabled: IS.chrome || !IS.mobile
    });
  }

  init() {
    this.maxPrintTime = 0;
    this.largeObjectArray = createLargeObjectArray();
    // 重置新增統計
    this.samples = 0;
    this.logEma = null;
    this.tableEma = null;
    this.ratioBaselineEma = null;
    this.consecutiveBreaches = 0;
    this.lastTriggerTime = 0;
    this.lastSampleTs = 0;
    this.wasHidden = false;
  }

  private updateEma(current: number, ema: number | null, alpha: number) {
    return ema == null ? current : (ema * (1 - alpha) + current * alpha);
  }

  // 檢查 document.hidden，SSR 安全處理
  private isHidden(): boolean {
    if (typeof document === 'undefined' || typeof document.hidden === 'undefined') return false;
    return document.hidden;
  }

  // 計算自適應差值門檻：隨 logEma 略成長，避免在高性能低延遲環境錯過比例型現象
  private computeAdaptiveDelta(logEma: number): number {
    const adaptive = Math.min(this.minDeltaMax, Math.max(this.minDeltaBase, logEma * 2));
    return adaptive;
  }

  detect() {
    // A: 背景分頁直接跳過採樣，但標記 wasHidden
    if (this.isHidden()) {
      this.wasHidden = true;
      return;
    }

    const now = Date.now();

    const tablePrintTime = calculateTime(() => { table(this.largeObjectArray); });
    const logPrintTime = calculateTime(() => { log(this.largeObjectArray); });

    // 向後相容保留原始最大 log 時間
    this.maxPrintTime = Math.max(this.maxPrintTime, logPrintTime);

    clearLog();

    if (tablePrintTime === 0 || logPrintTime === 0) {
      return; // 無效樣本
    }

    // B: gap-based 軟重置 (不清 baseline / ema)，僅重置連續計數與樣本計數避免暖身造成漏判
    const gap = this.lastSampleTs ? (now - this.lastSampleTs) : 0;
    const resumedFromHidden = this.wasHidden; // 回前景第一個樣本
    if (resumedFromHidden || gap > this.gapThresholdMs) {
      this.consecutiveBreaches = 0;
      // 把 samples 調整到暖身之後 (等於不進入暖身判斷)，避免高成本樣本被忽略
      this.samples = this.warmupSamples;
      this.wasHidden = false;
    }

    this.lastSampleTs = now;

    this.samples++;

    // 暖身期間僅建立初始基準，不做判斷
    if (this.samples <= this.warmupSamples) {
      this.logEma = this.updateEma(logPrintTime, this.logEma, this.emaAlpha);
      this.tableEma = this.updateEma(tablePrintTime, this.tableEma, this.emaAlpha);
      const warmupRatio = tablePrintTime / logPrintTime;
      this.ratioBaselineEma = this.updateEma(warmupRatio, this.ratioBaselineEma, this.baselineAlpha);
      return;
    }

    // 更新 EMA
    this.logEma = this.updateEma(logPrintTime, this.logEma, this.emaAlpha);
    this.tableEma = this.updateEma(tablePrintTime, this.tableEma, this.emaAlpha);

    if (!this.logEma || !this.tableEma) return; // 理論上不會

    const ratio = this.tableEma / this.logEma;

    // 正常情況下更新基準比值（若當前未出現極端偏高）
    if (this.ratioBaselineEma == null) {
      this.ratioBaselineEma = ratio; // 第一次建立
    } else {
      const upperGuard = this.ratioBaselineEma * 1.8; // 排除過度偏離值更新基準
      if (ratio < upperGuard) {
        this.ratioBaselineEma = this.updateEma(ratio, this.ratioBaselineEma, this.baselineAlpha);
      }
    }

    // 計算門檻：靜態與動態取最大值（避免基準過小時太敏感）
    const dynamicThreshold = (this.ratioBaselineEma ?? 1) * this.dynamicMultiplier;
    const ratioThreshold = Math.max(this.staticRatioThreshold, dynamicThreshold);
    const delta = this.tableEma - this.logEma;
    const adaptiveDelta = this.computeAdaptiveDelta(this.logEma);
    const deltaOk = delta >= adaptiveDelta;

    // D: 雙閾值機制（比例達標且差值足夠，或比例超過 emergency）
    const isBreach = this.tableEma > this.minTableMs && ratio > ratioThreshold && (deltaOk || ratio >= this.highRatioEmergency);

    if (isBreach) {
      this.consecutiveBreaches++;
    } else {
      this.consecutiveBreaches = 0; // reset 連續計數
    }

    // 與舊邏輯的保底：極端值仍可觸發（保留原判斷）
    // 背景 legacyExtreme 抑制：僅在前景判斷 legacyExtreme（此處一定是前景）
    const legacyExtreme = !resumedFromHidden && (tablePrintTime > this.maxPrintTime * 10 && tablePrintTime > this.minTableMs);

    const cooledDown = now - this.lastTriggerTime > this.cooldownMs;

    if (cooledDown && (legacyExtreme || this.consecutiveBreaches >= this.requiredConsecutiveBreaches)) {
      this.lastTriggerTime = now;
      this.consecutiveBreaches = 0;
      this.onDevToolOpen();
    }
  }
}